import {
  useCreateDevelopmentNote,
  useDevelopmentNotes,
} from "@coaching-culture/api-client";
import {
  Box,
  Button,
  EmptyState,
  Flex,
  IconToast,
  Loader,
  Modal,
  RichTextEditor,
  Text,
  useToast,
} from "@coaching-culture/ui";
import { Comment } from "components/Comment";
import { ScrollableContainer } from "components/ScrollableContainer";
import { useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperPlane,
  FaSeedling,
} from "react-icons/fa";

export type DevelopmentNotesModalProps = {
  relationshipId: string;
  onCancel: () => void;
};

export function DevelopmentNotesModal({
  relationshipId,
  onCancel,
}: DevelopmentNotesModalProps) {
  const { data: notes, isFetched } = useDevelopmentNotes(relationshipId);
  const createNote = useCreateDevelopmentNote();
  const pushToast = useToast();
  const [body, setBody] = useState("");

  const submit = async () => {
    const trimmed = body.trim();
    if (trimmed === "" || trimmed === "<p></p>") {
      pushToast({
        content: (
          <IconToast
            icon={FaExclamationCircle}
            text="Please write something before saving."
            iconColor="danger"
          />
        ),
      });
      return;
    }

    try {
      await createNote.mutateAsync({ relationshipId, body: trimmed });
      setBody("");
      pushToast({
        content: (
          <IconToast
            icon={FaCheckCircle}
            text="Development note saved"
            iconColor="primary"
          />
        ),
      });
    } catch {
      pushToast({
        content: (
          <IconToast
            icon={FaExclamationCircle}
            text="Could not save the note. Please try again."
            iconColor="danger"
          />
        ),
      });
    }
  };

  return (
    <Modal width={900} showCloseButton onClose={onCancel} p={5}>
      <Text fontSize={5} fontWeight={600} mb={2}>
        Development Notes
      </Text>
      <Text color="textMuted" mb={3}>
        Notes are shared between the manager and direct report, and span every
        conversation in this relationship.
      </Text>

      {!isFetched ? (
        <Flex justifyContent="center" p={3}>
          <Loader />
        </Flex>
      ) : notes && notes.length > 0 ? (
        <ScrollableContainer mb={3} maxHeight={400}>
          {notes.map((note) => (
            <Box key={note.id} pr={2}>
              <Comment comment={note} />
            </Box>
          ))}
        </ScrollableContainer>
      ) : (
        <EmptyState icon={FaSeedling} compact mb={3}>
          No development notes yet. Add the first one below.
        </EmptyState>
      )}

      <RichTextEditor value={body} onChange={setBody} height={250} />
      <Flex mt={2} justifyContent="flex-end">
        <Button
          onClick={submit}
          color="primary"
          icon={FaPaperPlane}
          disabled={createNote.isLoading}
        >
          Save Note
        </Button>
      </Flex>
    </Modal>
  );
}
